/**
 * React Error Boundary with OpenTelemetry error capture.
 *
 * Catches unhandled React rendering errors, records them as OTel spans
 * (with sanitized attributes — no PII), and renders a user-friendly
 * fallback UI consistent with the app's styling.
 *
 * @module E3 – Browser error capture
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { SpanStatusCode } from "@opentelemetry/api";
import { getTracer } from "@/telemetry";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback. Receives the error so it can display a message. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Sanitize an error for OTel recording.
 *
 * We intentionally record only:
 *  - `exception.type`    — error constructor name (e.g. "TypeError")
 *  - `exception.message` — the message string
 *
 * We **omit**:
 *  - Full stack traces (may contain file paths with user names)
 *  - Raw URLs / query params (may contain tokens or PII)
 *
 * React `componentStack` is included but truncated to 200 chars (framework-
 * internal, no PII risk).
 */
function recordErrorSpan(
  spanName: string,
  error: unknown,
  extras?: Record<string, string | number>,
): void {
  const tracer = getTracer("floline-web.error-capture");
  const span = tracer.startSpan(spanName);

  const errName =
    error instanceof Error ? error.constructor.name : "UnknownError";
  const errMessage =
    error instanceof Error ? error.message : String(error);

  span.setAttributes({
    "exception.type": errName,
    "exception.message": errMessage,
    ...extras,
  });

  span.setStatus({ code: SpanStatusCode.ERROR, message: errMessage });

  // `recordException` adds a standard "exception" event to the span.
  // We pass a trimmed object so the SDK does not serialise the full stack.
  span.recordException({ name: errName, message: errMessage });

  span.end();
}

// ---------------------------------------------------------------------------
// Default fallback UI
// ---------------------------------------------------------------------------

function DefaultFallback({
  error,
  onReset,
}: {
  error: Error;
  onReset: () => void;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="mx-auto max-w-md text-center">
        {/* Icon */}
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-8 w-8 text-destructive"
            aria-hidden="true"
            role="img"
          >
            <title>Error</title>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        <h1 className="mb-2 text-2xl font-semibold tracking-tight text-foreground">
          Something went wrong
        </h1>
        <p className="mb-6 text-sm text-muted-foreground">
          An unexpected error occurred. You can try again or reload the page.
        </p>

        {/* Error detail — only the name + message, no stack */}
        {import.meta.env.DEV && (
          <pre className="mb-6 max-h-40 overflow-auto rounded-lg border border-border bg-muted p-4 text-left text-xs text-muted-foreground">
            {error.name}: {error.message}
          </pre>
        )}

        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={onReset}
            className="inline-flex h-10 items-center justify-center rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex h-10 items-center justify-center rounded-lg border border-input bg-background px-4 text-sm font-semibold text-foreground shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Reload page
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ErrorBoundary class component
// ---------------------------------------------------------------------------

/**
 * React class-based Error Boundary.
 *
 * Wraps the entire app (or a subtree) and catches errors thrown during
 * rendering, lifecycle methods, and constructors of the tree below.
 *
 * On catch it:
 *  1. Records an OTel span (`ui.error_boundary`) with sanitized error info.
 *  2. Renders a fallback UI (customisable via the `fallback` prop).
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Record the error as an OTel span.
    // We pass the React component name (if available) as a safe extra attribute.
    recordErrorSpan("ui.error_boundary", error, {
      "react.lifecycle": "componentDidCatch",
      // componentStack can be long but is framework-internal (no PII).
      // We record only a truncated version to aid debugging without bloating spans.
      ...(info.componentStack
        ? {
            "react.component_stack_preview": info.componentStack
              .trim()
              .slice(0, 200),
          }
        : {}),
    });

    // Let the default console.error still fire for dev visibility.
    console.error("[ErrorBoundary]", error, info);
  }

  /** Reset the boundary so the children can re-render. */
  private handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;

    if (error) {
      if (this.props.fallback) {
        return this.props.fallback(error, this.handleReset);
      }
      return <DefaultFallback error={error} onReset={this.handleReset} />;
    }

    return this.props.children;
  }
}

// Re-export the helper so the global-listeners module can share the same logic.
export { recordErrorSpan };
